import { useAuth0 } from '@auth0/auth0-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { API_URL, apiFetch } from '@/config/api';

import type {
  DeleteOnlineEvalIssuesResponse,
  IssueCategoriesVersion,
  IssueCategory,
  IssueCategoryInput,
  IssueCategoryUpdate,
  OnlineEvalAgentCountsResponse,
  OnlineEvalIssueChat,
  OnlineEvalIssueRequest,
  OnlineEvalIssueFilters,
  OnlineEvalAdHocRun,
  OnlineEvalAdHocRunPreview,
  OnlineEvalAdHocRunRequest,
  OnlineEvalDebugSkeleton,
  OnlineEvalEvaluation,
  OnlineEvalIssuesResponse,
} from './online-eval.types';

const request = async <T>(
  url: string,
  token: string,
  init?: RequestInit
): Promise<T> => {
  const response = await apiFetch(url, {
    ...init,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...init?.headers,
    },
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as {
      detail?: string;
    } | null;
    throw new Error(body?.detail ?? `Request failed (${response.status})`);
  }
  return response.json() as Promise<T>;
};

export const useIssueCategories = () => {
  const { getAccessTokenSilently } = useAuth0();
  return useQuery({
    queryKey: ['online-eval', 'categories'],
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      return request<IssueCategory[]>(
        `${API_URL}/online_eval/issue_categories`,
        token
      );
    },
  });
};

export const useOnlineEvalEvaluators = () => {
  const { getAccessTokenSilently } = useAuth0();
  return useQuery({
    queryKey: ['online-eval', 'evaluators'],
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      return request<string[]>(`${API_URL}/online_eval/evaluators`, token);
    },
  });
};

// Refreshes while a run is running, so its progress and status update on their own
export const useOnlineEvalAdHocRuns = () => {
  const { getAccessTokenSilently } = useAuth0();
  return useQuery({
    queryKey: ['online-eval', 'ad-hoc-runs'],
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      return request<OnlineEvalAdHocRun[]>(
        `${API_URL}/online_eval/ad_hoc_runs`,
        token
      );
    },
    refetchInterval: (query) =>
      query.state.data?.some((run) => run.status === 'running') ? 5000 : false,
  });
};

export const usePreviewOnlineEvalAdHocRun = () => {
  const { getAccessTokenSilently } = useAuth0();
  return useMutation({
    mutationFn: async (input: OnlineEvalAdHocRunRequest) => {
      const token = await getAccessTokenSilently();
      return request<OnlineEvalAdHocRunPreview>(
        `${API_URL}/online_eval/ad_hoc_runs/preview`,
        token,
        { method: 'POST', body: JSON.stringify(input) }
      );
    },
  });
};

export const useStartOnlineEvalAdHocRun = () => {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: OnlineEvalAdHocRunRequest) => {
      const token = await getAccessTokenSilently();
      return request<OnlineEvalAdHocRun>(
        `${API_URL}/online_eval/ad_hoc_runs`,
        token,
        { method: 'POST', body: JSON.stringify(input) }
      );
    },
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['online-eval', 'ad-hoc-runs'],
        }),
        queryClient.invalidateQueries({
          queryKey: ['online-eval', 'evaluations'],
        }),
      ]),
  });
};

export const useOnlineEvalEvaluations = () => {
  const { getAccessTokenSilently } = useAuth0();
  return useQuery({
    queryKey: ['online-eval', 'evaluations'],
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      return request<OnlineEvalEvaluation[]>(
        `${API_URL}/online_eval/evaluations`,
        token
      );
    },
  });
};

// Under the categories key, so the category mutations' invalidation refetches it too
export const useIssueCategoryVersions = () => {
  const { getAccessTokenSilently } = useAuth0();
  return useQuery({
    queryKey: ['online-eval', 'categories', 'versions'],
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      return request<IssueCategoriesVersion[]>(
        `${API_URL}/online_eval/issue_category_versions`,
        token
      );
    },
  });
};

const toIsoOrEmpty = (dateTimeLocal: string): string =>
  dateTimeLocal ? new Date(dateTimeLocal).toISOString() : '';

// The filters the agent comparison counts honor: every one but agent and category, which
// the page applies to the charts itself. A list filter repeats its parameter once per value.
const chartFilterParams = (filters: OnlineEvalIssueFilters) =>
  new URLSearchParams(
    [
      ...filters.models.map((model) => ['model', model]),
      ['user', filters.user],
      ['created_after', toIsoOrEmpty(filters.createdAfter)],
      ['created_before', toIsoOrEmpty(filters.createdBefore)],
      ['search', filters.search],
      ['min_confidence', filters.minConfidence],
      ['evaluator', filters.evaluator],
      ['issue_categories_version_id', filters.issueCategoriesVersionId],
    ].filter(([, value]) => value !== '')
  );

// Every filter but the page. The table and deletion honor the same ones, so deletion
// removes what the table shows.
const issueFilterParams = (filters: OnlineEvalIssueFilters) => {
  const params = chartFilterParams(filters);
  filters.categoryIds.forEach((categoryId) =>
    params.append('category_id', categoryId)
  );
  filters.agents.forEach((agent) => params.append('agent', agent));
  return params;
};

// The backend refuses to delete without a filter, since that would delete every finding.
// The evaluation doesn't count: the backend always fills it in.
export const hasIssueFilters = (filters: OnlineEvalIssueFilters) =>
  issueFilterParams({
    ...filters,
    evaluator: '',
    issueCategoriesVersionId: '',
  }).toString() !== '';

export const useOnlineEvalIssues = (filters: OnlineEvalIssueFilters) => {
  const { getAccessTokenSilently } = useAuth0();
  return useQuery({
    queryKey: ['online-eval', 'issues', filters],
    queryFn: async () => {
      const params = issueFilterParams(filters);
      params.set('page', String(filters.page));
      params.set('limit', '100');
      const token = await getAccessTokenSilently();
      return request<OnlineEvalIssuesResponse>(
        `${API_URL}/online_eval/issues?${params.toString()}`,
        token
      );
    },
    placeholderData: (previous) => previous,
  });
};

export const useDeleteOnlineEvalIssues = () => {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (filters: OnlineEvalIssueFilters) => {
      const token = await getAccessTokenSilently();
      return request<DeleteOnlineEvalIssuesResponse>(
        `${API_URL}/online_eval/issues?${issueFilterParams(filters).toString()}`,
        token,
        { method: 'DELETE' }
      );
    },
    onSuccess: async () => {
      await Promise.all(
        ['issues', 'agent-counts', 'categories'].map((key) =>
          queryClient.invalidateQueries({ queryKey: ['online-eval', key] })
        )
      );
    },
  });
};

// Served apart from the listing: its denominator scans every evaluated chat's
// replies, so the table should never wait on it, and paging or narrowing by agent
// or category should not recompute it
export const useOnlineEvalAgentCounts = (filters: OnlineEvalIssueFilters) => {
  const { getAccessTokenSilently } = useAuth0();
  const params = chartFilterParams(filters);
  return useQuery({
    queryKey: ['online-eval', 'agent-counts', params.toString()],
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      return request<OnlineEvalAgentCountsResponse>(
        `${API_URL}/online_eval/issues/agent_counts?${params.toString()}`,
        token
      );
    },
    placeholderData: (previous) => previous,
  });
};

export const useOnlineEvalDebugSkeleton = (
  issueId: string | null,
  enabled: boolean
) => {
  const { getAccessTokenSilently } = useAuth0();
  return useQuery({
    queryKey: ['online-eval', 'debug-skeleton', issueId],
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      return request<OnlineEvalDebugSkeleton | null>(
        `${API_URL}/online_eval/issues/${issueId}/debug_skeleton`,
        token
      );
    },
    enabled: enabled && issueId !== null,
    staleTime: Infinity,
    gcTime: Infinity,
  });
};

// Conversation content, which the backend serves only for the admin's own chats.
// A finding on anyone else's chat answers 403, and the sheet falls back to the skeleton.
export const useOnlineEvalIssueChat = (
  issueId: string | null,
  enabled: boolean
) => {
  const { getAccessTokenSilently } = useAuth0();
  return useQuery({
    queryKey: ['online-eval', 'issue-chat', issueId],
    queryFn: async (): Promise<OnlineEvalIssueChat | null> => {
      const token = await getAccessTokenSilently();
      const response = await apiFetch(
        `${API_URL}/online_eval/issues/${issueId}/chat`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.status === 403) return null;
      if (!response.ok) {
        const body = (await response.json().catch(() => null)) as {
          detail?: string;
        } | null;
        throw new Error(body?.detail ?? `Request failed (${response.status})`);
      }
      return response.json() as Promise<OnlineEvalIssueChat>;
    },
    enabled: enabled && issueId !== null,
    staleTime: Infinity,
    gcTime: Infinity,
  });
};

// What the evaluator was asked, which the backend keeps only for internal users' chats, for 20
// days, and serves only to the admin whose chat it is. A 404 means none was kept for the finding.
export const useOnlineEvalIssueRequest = (
  issueId: string | null,
  enabled: boolean
) => {
  const { getAccessTokenSilently } = useAuth0();
  return useQuery({
    queryKey: ['online-eval', 'issue-request', issueId],
    queryFn: async (): Promise<OnlineEvalIssueRequest | null> => {
      const token = await getAccessTokenSilently();
      const response = await apiFetch(
        `${API_URL}/online_eval/issues/${issueId}/request`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.status === 404) return null;
      if (!response.ok) {
        const body = (await response.json().catch(() => null)) as {
          detail?: string;
        } | null;
        throw new Error(body?.detail ?? `Request failed (${response.status})`);
      }
      return response.json() as Promise<OnlineEvalIssueRequest>;
    },
    enabled: enabled && issueId !== null,
    staleTime: Infinity,
    gcTime: Infinity,
  });
};

export const useCreateIssueCategory = () => {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: IssueCategoryInput) => {
      const token = await getAccessTokenSilently();
      return request<IssueCategory>(
        `${API_URL}/online_eval/issue_categories`,
        token,
        { method: 'POST', body: JSON.stringify(input) }
      );
    },
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: ['online-eval', 'categories'],
      }),
  });
};

export const useUpdateIssueCategory = () => {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      input,
    }: {
      id: string;
      input: IssueCategoryUpdate;
    }) => {
      const token = await getAccessTokenSilently();
      return request<IssueCategory>(
        `${API_URL}/online_eval/issue_categories/${id}`,
        token,
        { method: 'PATCH', body: JSON.stringify(input) }
      );
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['online-eval', 'categories'],
        }),
        queryClient.invalidateQueries({ queryKey: ['online-eval', 'issues'] }),
      ]);
    },
  });
};
